// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import type { SurveyType } from '../../api/types';
import { CaseDataProvider, useCaseStore } from '../../app/bygning/CaseDataContext';
import { useCaseShell } from '../../app/bygning/CaseShellContext';
import { GeometriEditorProvider, type GeometriEditorHandle } from '../../app/bygning/GeometriEditorContext';
import { ShellNotices } from '../../app/bygning/ShellNotices';
import { storedFlags } from '../../app/featureFlags';
import { kindOf } from '../../app/keyTargets';
import { ALL_CASES_PATH, EXPERT_ENTRIES, badgeText } from '../../app/navigation';
import { createOnceGuard } from '../../app/onceGuard';
import { useToast } from '../../app/useToast';
import { blockReasons, blockingItems, heldBlockingIds } from '../../bygning/blocking';
import { escapePatch, shellKeyAction } from '../../bygning/keys';
import { MODES, badgeCount, modeByPath, modePath, modeTabByPath, visibleModes, type ModeId } from '../../bygning/modes';
import { accountScope, buildCommands, filterCommands, type Command } from '../../bygning/palette';
import { leafOf, leafPatch, treeLeaves } from '../../bygning/rapportTree';
import { redigerHref, stripRedigerKeys } from '../../bygning/rediger';
import { activeFilterCount, filterResources, matchType, type ResourceFilter } from '../../bygning/resourceFilter';
import { roomIds, roomName, roomsOf } from '../../bygning/rooms';
import { applyPatch, format, parse, step, storeyPatch, switchMode, type Selection } from '../../bygning/selection';
import { storeyById, storeyOfRoom } from '../../bygning/storeys';
import { ErrorBanner } from '../../components/ErrorBanner';
import { Spinner } from '../../components/Spinner';
import { Toast } from '../../components/Toast';
import { CommandPalette, type PaletteItem } from '../../components/bygning/CommandPalette';
import { Crumb } from '../../components/bygning/Crumb';
import { SubTabs } from '../../components/bygning/SubTabs';
import { Tile } from '../../components/bygning/Tile';
import { TopBar } from '../../components/bygning/TopBar';
import { Ground } from './Ground';
import type { LeftInset, ModeComponent, ModeProps, ModeView } from './modeTypes';
import { ModelSettings, SettingsProvider } from '../../components/bygning/settings/ModelSettings';
import { useSettingsDispatch, useStoreys } from '../../components/bygning/settings/SettingsContext';
import { MiljoeMode } from './MiljoeMode';
import { OverblikMode } from './OverblikMode';
import { GennemgangMode } from './GennemgangMode';
import { RapportMode } from './RapportMode';
import { RedigerMode } from './RedigerMode';
import { RessourcerMode, addManualType } from './RessourcerMode';
import styles from './BygningShell.module.css';

/** The mode implementations, one `ModeComponent` per tab. */
const MODE_COMPONENTS: Record<ModeId, ModeComponent> = {
  overblik: OverblikMode,
  ressourcer: RessourcerMode,
  gennemgang: GennemgangMode,
  miljoe: MiljoeMode,
  rediger: RedigerMode,
  rapport: RapportMode,
};

/**
 * Selection keys whose change is a new focus (pushes history); the rest are
 * view toggles (replace). Opening the Sagsdata dialog (`edit`) is one, so
 * Back closes it; so is a leaf of Rapporter & indberetning's tree (`page`,
 * `rapport`).
 */
const FOCUS_KEYS: readonly (keyof Selection)[] = ['typeId', 'partCode', 'sampleId', 'room', 'storey', 'page', 'rapport', 'edit'];

/** How long the Ground waits for Ressourcer's filters to settle before it re-ghosts the cloud. */
const GHOST_DEBOUNCE_MS = 150;

/**
 * Bygning først: the scanned building as the ground layer, the top bar and
 * four tile slots floating over it (PLAN.md "Layout contract"). One instance
 * serves all five mode routes (a layout route in App.tsx), so the Ground and
 * the case store stay mounted while the mode changes.
 */
export function BygningShell() {
  const toast = useToast(2600);
  // The settings provider wraps the whole body (not just the Ground and the
  // gear panel) so the shell itself can read the Ground's derived storeys
  // (`useStoreys`) for the breadcrumb, J/K and the palette.
  return (
    <CaseDataProvider notify={toast.show}>
      <SettingsProvider>
        <ShellBody toastMessage={toast.message} />
      </SettingsProvider>
    </CaseDataProvider>
  );
}

/**
 * The tiles for a view, and which left tile among them (the Ground's overlay
 * steps right of it — or, on a phone, under it when it is a bar).
 */
interface Slots {
  tiles: ReactNode;
  leftInset: LeftInset;
  phoneBar: boolean;
}

/**
 * Calls the mode as a hook (keyed by mode, so its hooks reset on a switch),
 * lays out its view and reports the left tile up to the shell — before
 * paint, so the Ground's overlay never shows at the old inset.
 */
function ModeHost({
  impl,
  props,
  render,
  onLeftInset,
}: {
  impl: ModeComponent;
  props: ModeProps;
  render: (view: ModeView) => Slots;
  onLeftInset: (inset: LeftInset, phoneBar: boolean) => void;
}) {
  const view = impl(props);
  const { tiles, leftInset, phoneBar } = render(view);
  useLayoutEffect(() => onLeftInset(leftInset, phoneBar), [onLeftInset, leftInset, phoneBar]);
  return <>{tiles}</>;
}

function ShellBody({ toastMessage }: { toastMessage: string | null }) {
  const { data, actions } = useCaseStore();
  const shell = useCaseShell();
  const location = useLocation();
  const navigate = useNavigate();
  const modeDef = modeByPath(location.pathname) ?? MODES[0];
  const mode = modeDef.id;
  // A mode's sub-tab (Rediger › Scan & pose …), null on the mode's own path.
  const subTab = modeTabByPath(location.pathname)?.tab ?? null;
  const subTabDef = modeDef.tabs?.find((t) => t.id === subTab) ?? null;
  // Rediger › Scan & pose draws its own canvas over the Ground: no Modelindstillinger there.
  const covered = mode === 'rediger' && subTab === 'scan';
  const gearOffered = modeDef.gear && !covered;

  // ---------------------------------------------------------- selection --

  const [selParts, setSelParts] = useState<string[]>([]);
  const sel = useMemo<Selection>(() => ({ ...parse(location.search, mode), selParts }), [location.search, mode, selParts]);
  // Mirrors for callbacks that outlive a render (keyboard, palette, queued writes).
  const selRef = useRef(sel);
  selRef.current = sel;
  const locationRef = useRef(location);
  locationRef.current = location;

  // Picked parts belong to the mode they were picked in.
  useEffect(() => setSelParts([]), [mode]);

  /**
   * Applies a selection patch to the URL. A focus change pushes history, a
   * view toggle replaces; `opts.replace` forces replace (a default the shell
   * fills in, not a step the user took).
   */
  const setSel = useCallback(
    (patch: Partial<Selection>, opts?: { replace?: boolean }) => {
      const cur = selRef.current;
      const { mode: toMode, ...rest } = patch;
      const base = toMode && toMode !== cur.mode ? switchMode(cur, toMode) : cur;
      const next = applyPatch(base, rest);
      if (next.selParts !== cur.selParts) setSelParts(next.selParts);
      const loc = locationRef.current;
      // Within a mode the sub-tab stays (a patch never moves Rediger off /rediger/scan).
      const pathname = next.mode === cur.mode ? loc.pathname : modePath(next.mode);
      // Rediger's own keys (?edge=, ?source=, …) stay behind when the user leaves it.
      const search = format(next, cur.mode === 'rediger' && next.mode !== 'rediger' ? stripRedigerKeys(loc.search) : loc.search);
      if (pathname === loc.pathname && search === loc.search.replace(/^\?/, '')) return;
      const focus = pathname !== loc.pathname || FOCUS_KEYS.some((k) => next[k] !== cur[k]);
      navigate({ pathname, search: search ? `?${search}` : '' }, { replace: opts?.replace === true || !focus });
    },
    [navigate],
  );

  const tabHref = useCallback(
    (id: ModeId) => {
      const keep = mode === 'rediger' && id !== 'rediger' ? stripRedigerKeys(location.search) : location.search;
      const search = format(switchMode(sel, id), keep);
      return search ? `${modePath(id)}?${search}` : modePath(id);
    },
    [sel, mode, location.search],
  );
  /** A sub-tab's link: the query travels, minus Rediger's per-tab keys. */
  const subTabHref = useCallback(
    (tab: string) => {
      const search = stripRedigerKeys(location.search);
      return search ? `${modePath(mode)}/${tab}?${search}` : `${modePath(mode)}/${tab}`;
    },
    [mode, location.search],
  );

  // ------------------------------------------------------- derived data --

  const rooms = useMemo(() => roomsOf(data.types, data.summary), [data.types, data.summary]);
  const storeys = useStoreys();
  const counts = data.survey?.counts ?? null;
  const pendingSamples = data.loaded ? data.samples.filter((s) => s.stage !== 'svar').length : data.summary?.pending_samples;
  // What blocks the report: Gennemgang's list, its badge and its J/K order.
  const blocking = useMemo(() => blockingItems(data.types, data.samples, data.fractions), [data.types, data.samples, data.fractions]);
  const badgeCounts = {
    queue: counts?.queue ?? data.summary?.counts.queue,
    pendingSamples,
    blocking: data.loaded ? blocking.length : data.fractions?.blocking_types,
  };
  // The feature flags (`app/featureFlags.ts`): whether the tree has the Materialeflow leaf.
  const [flags] = useState(storedFlags);
  const treeFlags = useMemo(() => ({ materialeflow: flags.submission }), [flags]);

  // Ressourcer's filtered set (`filterResources`): what its strip, Tabel and
  // Galleri list, J/K walk and the Ground leaves in colour. The same set the
  // mode derives itself; keyed by the five filter fields, not the whole selection.
  const filter = useMemo<ResourceFilter>(
    () => ({ room: sel.room, search: sel.search, env: sel.env, starred: sel.starred, errors: sel.errors }),
    [sel.room, sel.search, sel.env, sel.starred, sel.errors],
  );
  const filtered = useMemo(
    () => (mode === 'ressourcer' ? filterResources(data.types, data.dismissed, filter) : null),
    [mode, data.types, data.dismissed, filter],
  );
  // With a filter set: the live parts outside the set (ghost in the model) and
  // the set's types (the legend). No filter, no ghosting — today's look, the
  // rejected types in their lens colour.
  const filtering = filtered !== null && activeFilterCount(filter) > 0;
  // Keyed by content, so a keystroke that leaves the same set does nothing.
  const ghostKey = useMemo(() => {
    if (!filtering || !filtered) return '';
    const shown = new Set(filtered.parts.map((r) => r.part.code));
    const out: string[] = [];
    for (const t of data.types) for (const p of t.parts) if (!shown.has(p.code)) out.push(p.code);
    return out.join('\n');
  }, [filtering, filtered, data.types]);
  // Debounced: recolouring the cloud is a pass over every point, so the Ground
  // follows the typing a beat behind (the strip and the lists do not wait).
  const [shownGhostKey, setShownGhostKey] = useState(ghostKey);
  useEffect(() => {
    if (ghostKey === shownGhostKey) return;
    const timer = setTimeout(() => setShownGhostKey(ghostKey), GHOST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [ghostKey, shownGhostKey]);
  const ghostCodes = useMemo(() => (shownGhostKey === '' ? undefined : shownGhostKey.split('\n')), [shownGhostKey]);
  const legendTypeIds = useMemo(
    () => (filtering && filtered ? new Set(filtered.types.map((m) => m.type.id)) : null),
    [filtering, filtered],
  );

  /**
   * The type list of Ressourcer (the filtered set's types, its strip) or
   * Gennemgang (the blocking types the case holds, its left tile), in the
   * modes' own order.
   */
  const typeStrip = useCallback(
    (m: ModeId): SurveyType[] => {
      if (m === 'ressourcer') return filtered?.types.map((x) => x.type) ?? [];
      if (m !== 'gennemgang') return [];
      const byId = new Map(data.types.map((t) => [t.id, t]));
      return heldBlockingIds(blocking).flatMap((id) => byId.get(id) ?? []);
    },
    [data.types, filtered, blocking],
  );

  /** The strip order J/K walks, per mode (the same order each mode's strip shows). */
  const stepper = useCallback(
    (delta: number): Partial<Selection> | null => {
      const s = selRef.current;
      switch (s.mode) {
        case 'ressourcer': {
          if (s.tview === 'dele') {
            // Dele: the set's parts, the selection's type following the part.
            const rows = filtered?.parts ?? [];
            const code = step(rows.map((r) => r.part.code), s.partCode, delta);
            const row = rows.find((r) => r.part.code === code);
            return row ? { typeId: row.type.id, partCode: row.part.code, selParts: s.selParts } : null;
          }
          // Within the filtered types; a filter that leaves none leaves the selection be.
          const ids = typeStrip(s.mode).map((t) => t.id);
          return ids.length > 0 ? { typeId: step(ids, s.typeId, delta) } : null;
        }
        case 'gennemgang':
          return { typeId: step(typeStrip(s.mode).map((t) => t.id), s.typeId, delta) };
        case 'miljoe':
          return { sampleId: step(data.samples.map((x) => x.id), s.sampleId, delta) };
        case 'overblik': {
          // Under a storey focus the strip shows only its rooms; J/K walk those.
          const storey = storeyById(storeys, s.storey);
          const ids = roomIds(rooms);
          return { room: step(storey ? ids.filter((id) => storey.roomIds.includes(id)) : ids, s.room, delta) };
        }
        case 'rapport': {
          // The tree's leaves, in tree order.
          const ids = treeLeaves(treeFlags).map((l) => l.id);
          const to = step(ids, leafOf(s.page, s.rapport, treeFlags).id, delta);
          return to ? leafPatch(to) : null;
        }
        default:
          return null;
      }
    },
    [typeStrip, filtered, data.samples, rooms, storeys, treeFlags],
  );

  // Landing on Ressourcer or Gennemgang with no ?type=: the inspector already
  // shows the list's first type, so put it in the URL too — G/A/J/K act on
  // `sel.typeId`. Replace, not push: the user did not step anywhere.
  useEffect(() => {
    if (!data.loaded || sel.typeId !== null) return;
    const first = typeStrip(mode)[0];
    if (first) setSel({ typeId: first.id }, { replace: true });
  }, [data.loaded, sel.typeId, mode, typeStrip, setSel]);

  // --------------------------------------------------------------- review --

  const reviewSelected = useCallback(
    (status: 'approved' | 'rejected') => {
      const s = selRef.current;
      const t = data.types.find((x) => x.id === s.typeId);
      // A held G/A must not stack requests (the old page's rule).
      if (!t || data.busy || t.review_status === status) return;
      // Ressourcer's strip is the filtered set's types; a type outside it keeps the selection.
      // Gennemgang's list is what blocks: a type that stops blocking moves the selection on.
      const inStrip = (x: SurveyType) =>
        s.mode === 'gennemgang' ? blockReasons(x, data.samples).length > 0 : matchType(x, s) !== null;
      const wasIn = s.mode === 'gennemgang' || inStrip(t);
      const list = (s.mode === 'gennemgang' ? typeStrip(s.mode) : data.types.filter(inStrip)).map((x) => x.id);
      const at = list.indexOf(t.id);
      const neighbour = list[at + 1] ?? list[at - 1] ?? null;
      void actions.review(t.id, status).then((body) => {
        if (body && wasIn && !inStrip(body) && selRef.current.typeId === t.id) setSel({ typeId: neighbour });
      });
    },
    [data.types, data.samples, data.busy, actions, setSel, typeStrip],
  );

  // ------------------------------------------------------ palette, gear --

  const [paletteOpen, setPaletteOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const settingsDispatch = useSettingsDispatch();
  // Rediger › Geometri's editor (frontend-u0o.9): the palette runs its commands through this.
  const geometriRef = useRef<GeometriEditorHandle>(null);
  const setElement = useCallback((guid: string | null) => setSel({ element: guid }), [setSel]);
  const setPlan = useCallback((plan: boolean) => settingsDispatch?.({ type: 'set', patch: { planLock: plan } }), [settingsDispatch]);
  const frameAll = useCallback(() => settingsDispatch?.({ type: 'frame' }), [settingsDispatch]);
  const addGuard = useRef(createOnceGuard());
  useEffect(() => {
    if (!gearOffered) setSettingsOpen(false);
  }, [gearOffered]);
  // A viewer has no Rediger tab or commands (a deep link still lands, read-only).
  const tabModes = useMemo(() => visibleModes(data.canEdit), [data.canEdit]);

  const commands = useMemo<Command[]>(
    () =>
      buildCommands({
        modes: tabModes,
        types: data.types,
        samples: data.samples,
        rooms,
        storeys,
        experts: EXPERT_ENTRIES,
        canEdit: data.canEdit,
        geometri: mode === 'rediger' && subTab === 'geometri',
        account: accountScope(shell.user, shell.viaShareLink),
        focus: sel.partCode ? { partCode: sel.partCode, mode: sel.mode === 'gennemgang' ? 'gennemgang' : 'ressourcer' } : undefined,
      }),
    [tabModes, data.types, data.samples, rooms, storeys, data.canEdit, mode, subTab, shell.user, shell.viaShareLink, sel.partCode, sel.mode],
  );
  const search = useCallback(
    (query: string): PaletteItem[] => filterCommands(commands, query).map((c) => ({ id: c.id, label: c.label, hint: c.hint })),
    [commands],
  );

  const runCommand = useCallback(
    (id: string) => {
      const cmd = commands.find((c) => c.id === id);
      if (!cmd) return;
      const t = cmd.target;
      switch (t.kind) {
        case 'mode':
          setSel({ mode: t.mode });
          return;
        case 'select':
          // A storey command drops a focused room that is on another storey.
          if (t.patch.storey !== undefined) {
            setSel({ mode: t.mode, ...t.patch, ...storeyPatch(storeys, t.patch.storey, selRef.current.room) });
          } else {
            setSel({ mode: t.mode, ...t.patch });
          }
          return;
        case 'navigate':
          navigate(t.to);
          return;
        case 'leave':
          window.location.assign(t.href);
          return;
        case 'view':
          settingsDispatch?.({ type: 'view', preset: t.preset });
          return;
        case 'geometri':
          geometriRef.current?.run(t.command);
          return;
        case 'action':
          switch (t.action) {
            case 'export-csv': {
              const a = document.createElement('a');
              a.href = api.csvExportUrl();
              a.download = '';
              a.click();
              return;
            }
            case 'add-manual': {
              // The strip's dashed card, run from anywhere: then focus the new type in Ressourcer.
              if (!data.canEdit || data.busy) return;
              const room = roomName(rooms, selRef.current.room);
              addGuard.current.run(async () => {
                const created = await addManualType(actions, room);
                if (created) setSel({ mode: 'ressourcer', typeId: created.id });
              });
              return;
            }
            case 'sync':
              void actions.syncSurvey();
              return;
            case 'generate-report':
              if (data.reportVersions === null) actions.notify('Versionslisten indlæses stadig — prøv igen om lidt');
              else void actions.generateReport(null);
              return;
            case 'toggle-theme':
              shell.theme.setPreference(shell.theme.resolved === 'dark' ? 'light' : 'dark');
              return;
            case 'settings':
              if (!gearOffered) setSel({ mode: 'ressourcer' });
              setSettingsOpen((o) => !o);
              return;
            case 'pick-point':
              // The tool is off in the quiet mode (Rapport): go where it works.
              if (mode === 'rapport') setSel({ mode: 'ressourcer' });
              settingsDispatch?.({ type: 'set', patch: { picking: true } });
              return;
            case 'toggle-projection':
              settingsDispatch?.({ type: 'toggle-projection' });
              return;
          }
      }
    },
    [commands, setSel, navigate, actions, data.canEdit, data.busy, data.reportVersions, rooms, storeys, shell.theme, gearOffered, mode, settingsDispatch],
  );

  // ------------------------------------------------------------ keyboard --

  const onKey = useRef<(e: KeyboardEvent) => void>(() => {});
  onKey.current = (e: KeyboardEvent) => {
    // A modal dialog (the Sagsdata form, Tilføj ressource, …) owns its keys:
    // no J/K stepping behind it and no palette over it. The palette is
    // itself a modal, so ⌘K still closes it.
    if (!paletteOpen && e.target instanceof Element && e.target.closest('[aria-modal="true"]')) return;
    const action = shellKeyAction({
      key: e.key,
      metaKey: e.metaKey,
      ctrlKey: e.ctrlKey,
      altKey: e.altKey,
      kind: kindOf(e.target),
      mode,
      paletteOpen,
    });
    if (!action) return;
    switch (action) {
      case 'palette':
        e.preventDefault();
        setPaletteOpen((o) => !o);
        return;
      case 'escape': {
        if (settingsOpen) {
          e.preventDefault();
          setSettingsOpen(false);
          return;
        }
        const patch = escapePatch(selRef.current);
        if (!patch) return;
        e.preventDefault();
        setSel(patch);
        return;
      }
      case 'next':
      case 'prev': {
        const patch = stepper(action === 'next' ? 1 : -1);
        if (!patch) return;
        e.preventDefault();
        setSel(patch);
        return;
      }
      case 'approve':
      case 'reject':
        e.preventDefault();
        reviewSelected(action === 'approve' ? 'approved' : 'rejected');
        return;
      case 'open':
        // Enter on a focused part opens its evidence (Foto).
        if (selRef.current.partCode !== null && (mode === 'ressourcer' || mode === 'gennemgang')) {
          e.preventDefault();
          setSel({ ev: 'foto' });
        }
        return;
    }
  };
  useEffect(() => {
    const listener = (e: KeyboardEvent) => onKey.current(e);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, []);

  useEffect(() => {
    const name = shell.projectName;
    const label = subTabDef ? `${modeDef.label} › ${subTabDef.label}` : modeDef.label;
    document.title = name ? `${label} · ${name} · ReUseX` : `${label} · ReUseX`;
  }, [modeDef.label, subTabDef, shell.projectName]);

  // ---------------------------------------------------------------- view --

  const modeProps: ModeProps = { data, sel, setSel, actions };
  // The left tile the mode's view draws (ModeHost reports it): where the Ground's overlay starts.
  const [leftInset, setLeftInset] = useState<LeftInset>('none');
  const [leftPhoneBar, setLeftPhoneBar] = useState(false);
  // Where a mode's `subbar` is drawn: the end of its sub-tab bar (a portal target).
  const [subbarSlot, setSubbarSlot] = useState<HTMLSpanElement | null>(null);
  const onLeftInset = useCallback((inset: LeftInset, phoneBar: boolean) => {
    setLeftInset(inset);
    setLeftPhoneBar(phoneBar);
  }, []);
  const focusedRoom = sel.room === null ? null : (roomName(rooms, sel.room) ?? `Rum ${sel.room}`);
  // The storey level: the focused storey, or — with a room focused — the
  // room's own storey, even when the room was focused directly.
  const focusedStorey = storeyById(storeys, sel.storey);
  const crumbStorey = sel.room !== null ? (storeyOfRoom(storeys, sel.room) ?? focusedStorey) : focusedStorey;

  const renderSlots = (view: ModeView): Slots => {
    const hasBottom = view.bottom !== undefined && view.bottom !== null && view.bottom !== false;
    const hasCentre = Boolean(view.centre);
    const centreFull = Boolean(view.centreFull);
    const hint = view.hint ?? subTabDef?.hint ?? modeDef.hint;
    if (view.cover) {
      // A cover is the whole view: no other slot, no Modelindstillinger.
      return {
        tiles: <div className={styles.cover}>{view.cover}</div>,
        leftInset: 'none',
        phoneBar: false,
      };
    }
    const showSettings = settingsOpen && gearOffered;
    const showLeft = Boolean(view.left) && (!hasCentre || (Boolean(view.leftWithCentre) && !centreFull)) && !showSettings;
    // Modelindstillinger is always the wide tile.
    const leftInset: LeftInset = showSettings ? 'wide' : showLeft ? (view.leftWide ? 'wide' : 'narrow') : 'none';
    // The centre starts right of a left tile that stays beside it (and under it on a phone, where it is a bar).
    const besideLeft = showLeft && hasCentre ? (view.leftWide ? 'wide' : 'narrow') : null;
    const tiles = (
      <>
        {showLeft && (
          <Tile slot="left" wide={view.leftWide} phoneBar={view.leftPhoneBar} stacked={view.stacked} stripless={!hasBottom}>
            {view.left}
          </Tile>
        )}
        {view.right && !centreFull && (
          <Tile
            slot="right"
            full={view.rightFull}
            stripless={!hasBottom}
            stacked={view.stacked}
            swapKey={`${sel.typeId}:${sel.sampleId}:${sel.page}:${sel.rapport}:${view.rightKey ?? ''}`}
            label="Detaljer"
            live
          >
            {view.right}
          </Tile>
        )}
        {hasCentre && (
          <Tile
            slot="centre"
            full={centreFull}
            solo={!view.right}
            stripless={!hasBottom}
            besideLeft={besideLeft}
            underBar={besideLeft !== null && Boolean(view.leftPhoneBar)}
            label="Arbejdsflade"
          >
            {view.centre}
          </Tile>
        )}
        {hasBottom && !centreFull && (
          <Tile slot="bottom" label="Bånd">
            {view.bottom}
          </Tile>
        )}
        {showSettings && (
          // Stacked: ModelSettings is its own Panel (fixed header, scrolling body), which also names it.
          <Tile slot="left" wide stacked stripless={!hasBottom}>
            <ModelSettings
              onClose={() => setSettingsOpen(false)}
              onImport={() => {
                setSettingsOpen(false);
                navigate(redigerHref('import', { source: 'speckle' }));
              }}
            />
          </Tile>
        )}
        {hint && hasBottom && !hasCentre && !showSettings && <span className={styles.hint}>{hint}</span>}
        {view.overlay && <div className={styles.overlay}>{view.overlay}</div>}
        {view.subbar && subbarSlot && createPortal(view.subbar, subbarSlot)}
      </>
    );
    return { tiles, leftInset, phoneBar: showLeft && Boolean(view.leftPhoneBar) };
  };

  return (
    <GeometriEditorProvider
      active={mode === 'rediger' && subTab === 'geometri' && !data.error}
      element={sel.element}
      setElement={setElement}
      setPlan={setPlan}
      frameAll={frameAll}
      notify={actions.notify}
      handleRef={geometriRef}
    >
    <div className={modeDef.tabs ? `${styles.root} ${styles.withSubbar}` : styles.root}>
      <Ground
        {...modeProps}
        mode={mode}
        leftInset={data.error ? 'none' : leftInset}
        leftPhoneBar={!data.error && leftPhoneBar}
        ghostCodes={ghostCodes}
        legendTypeIds={legendTypeIds}
      />

      <TopBar
        crumb={
          <Crumb
            casesHref={shell.viaShareLink ? undefined : ALL_CASES_PATH}
            caseName={shell.projectName}
            onCase={sel.room !== null || sel.storey !== null ? () => setSel({ room: null, storey: null }) : null}
            storey={
              crumbStorey
                ? {
                    name: crumbStorey.name,
                    current: sel.room === null,
                    onClick:
                      sel.room === null
                        ? () => setSel({ storey: null })
                        : () => setSel({ room: null, storey: crumbStorey.id }),
                  }
                : null
            }
            room={
              focusedRoom
                ? {
                    name: focusedRoom,
                    onClear: () => setSel({ room: null }),
                    back: focusedStorey?.name,
                  }
                : null
            }
          />
        }
        tabs={tabModes.map((m) => {
          const n = badgeCount(m, badgeCounts);
          return {
            id: m.id,
            label: m.label,
            shortLabel: m.shortLabel,
            href: tabHref(m.id),
            badge: badgeText(n),
            badgeTitle:
              m.badge === 'queue'
                ? `${n ?? 0} typer venter på gennemsyn`
                : m.badge === 'samples'
                  ? `${n ?? 0} prøver uden svar`
                  : m.badge === 'blocking'
                    ? `${n ?? 0} typer blokerer rapporten`
                    : undefined,
          };
        })}
        currentMode={mode}
        lens={modeDef.lens ? { value: sel.lens, onChange: (lens) => setSel({ lens }) } : null}
        view={modeDef.lens ? { value: sel.view, onChange: (view) => setSel({ view }) } : null}
        progress={counts ? { approved: counts.approved, total: counts.all } : null}
        gear={gearOffered ? { pressed: settingsOpen, onToggle: () => setSettingsOpen((o) => !o) } : null}
        onPalette={() => setPaletteOpen(true)}
        user={shell.user && shell.logout ? { user: shell.user, onLogout: shell.logout, viaShareLink: shell.viaShareLink } : null}
      />

      {modeDef.tabs && (
        <SubTabs
          mode={modeDef.label}
          tabs={modeDef.tabs.map((t) => ({ id: t.id, label: t.label, href: subTabHref(t.id) }))}
          current={subTab}
        >
          <span ref={setSubbarSlot} className={styles.subbarSlot} />
        </SubTabs>
      )}

      <div className={styles.notes}>
        <ShellNotices variant="chips" />
        {data.staleNotice && (
          <p className={styles.note} role="alert">
            {data.staleNotice}
          </p>
        )}
        {data.loading && !data.loaded && !data.error && (
          <div className={styles.note}>
            <Spinner label="Indlæser sagen…" />
          </div>
        )}
      </div>

      {data.error ? (
        <Tile slot="centre" solo stripless label="Fejl">
          <ErrorBanner error={data.error} onRetry={actions.refresh} context="sagen" />
        </Tile>
      ) : (
        <ModeHost key={mode} impl={MODE_COMPONENTS[mode]} props={modeProps} render={renderSlots} onLeftInset={onLeftInset} />
      )}

      <CommandPalette open={paletteOpen} search={search} onRun={runCommand} onClose={() => setPaletteOpen(false)} />
      <Toast message={toastMessage} />
    </div>
    </GeometriEditorProvider>
  );
}
